import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Brain,
  Code2,
  BarChart3,
  Cloud,
  ArrowRight,
  Sparkles,
  Eye,
  MessageSquare,
  TrendingUp,
  Shield,
  Smartphone,
  Globe,
  Server,
  Palette,
  Database,
  PieChart,
  Activity,
  GitBranch,
  Container,
  Layers
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AnimatedSection, FadeIn, AnimatedContainer, AnimatedItem } from "@/components/AnimatedSection";
import SEO from "@/components/SEO";

const services = [
  {
    id: "ai-ml",
    icon: Brain,
    title: "AI & Machine Learning",
    description: "Transform your business with intelligent automation and advanced AI capabilities",
    color: "bg-blue-50 border-[#243b7f]",
    iconColor: "text-[#243b7f]",
    subServices: [
      { icon: Sparkles, name: "Generative AI", desc: "LLMs, VLMs, and custom generative models" },
      { icon: Eye, name: "Computer Vision Software Development", desc: "Image and video analysis solutions" },
      { icon: MessageSquare, name: "NLP & Conversational AI", desc: "Chatbots and language processing" },
      { icon: TrendingUp, name: "Predictive Analytics", desc: "Data-driven forecasting and insights" },
      { icon: Shield, name: "Responsible AI", desc: "Ethical and explainable AI practices" },
    ],
  },
  {
    id: "fullstack",
    icon: Code2,
    title: "Full-Stack Engineering",
    description: "End-to-end product development from concept to deployment",
    color: "bg-amber-50 border-amber-500",
    iconColor: "text-amber-600",
    subServices: [
      { icon: Smartphone, name: "Mobile Apps", desc: "iOS, Android, and Flutter development" },
      { icon: Globe, name: "Web Applications", desc: "Modern, responsive web experiences" },
      { icon: Server, name: "Backend & APIs", desc: "Scalable server-side solutions" },
      { icon: Cloud, name: "Cloud-native Architecture", desc: "Microservices and serverless design" },
      { icon: Palette, name: "UI/UX Systems", desc: "Beautiful, intuitive interfaces" },
    ],
  },
  {
    id: "analytics",
    icon: BarChart3,
    title: "Analytics & Data Engineering",
    description: "Turn your data into actionable insights and competitive advantage",
    color: "bg-emerald-50 border-emerald-500",
    iconColor: "text-emerald-600",
    subServices: [
      { icon: PieChart, name: "BI Tools", desc: "Power BI, Tableau, Looker implementations" },
      { icon: Database, name: "Data Warehousing & ETL", desc: "Efficient data pipelines and storage" },
      { icon: Activity, name: "Real-time Dashboards", desc: "Live monitoring and reporting" },
      { icon: TrendingUp, name: "BI Strategy", desc: "Data-driven decision frameworks" },
    ],
  },
  {
    id: "cloud",
    icon: Cloud,
    title: "Cloud & DevOps",
    description: "Robust infrastructure and deployment automation for scale",
    color: "bg-purple-50 border-purple-500",
    iconColor: "text-purple-600",
    subServices: [
      { icon: Cloud, name: "Multi-Cloud", desc: "AWS, Azure, GCP, and hybrid deployments" },
      { icon: GitBranch, name: "CI/CD", desc: "Automated testing and deployment" },
      { icon: Container, name: "Kubernetes", desc: "Container orchestration at scale" },
      { icon: Layers, name: "Microservices", desc: "Terraform and infrastructure as code" },
    ],
  },
];

export default function ServicesPage() {
  return (
    <div className="overflow-hidden" data-testid="services-page">
      <SEO
        title={"Enterprise AI & Software Engineering Services | VANCO"}
        description={"Explore VANCO services: Generative AI, Computer Vision software development services, Local RAG, Mobile & Web Full-Stack Engineering, and Power BI Analytics."}
        path="/services"
      />
      {/* Hero Section */}

      <section className="relative pt-32 pb-20 bg-gradient-to-br from-white via-slate-50 to-blue-50" data-testid="services-hero">
        <div className="absolute inset-0 overflow-hidden">

          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#243b7f]/5 rounded-full blur-3xl opacity-50" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#f7c550]/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block px-4 py-2 bg-[#243b7f]/10 text-[#243b7f] rounded-full text-sm font-medium mb-6">
              Our Services
            </span>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-[#0f172a] font-['Manrope'] mb-6">
              Comprehensive{" "}
              <span className="text-[#243b7f]">AI & Tech</span>{" "}
              Solutions
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              From artificial intelligence to full-stack development, we provide end-to-end technology services that drive real business outcomes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="relative py-24 md:py-32 bg-gradient-to-br from-white via-slate-50 to-blue-50" data-testid="services-grid">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-[#243b7f]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-0 w-[400px] h-[400px] bg-[#f7c550]/10 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="space-y-24">
            {services.map((service, index) => (
              <AnimatedSection key={service.id} delay={index * 0.1}>
                <div className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  {/* Service Header */}
                  <div className={`flex flex-col items-center text-center ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className={`inline-flex items-center gap-4 p-4 rounded-2xl ${service.color.split(' ')[0]} mb-6`}>
                      <service.icon className={`w-10 h-10 ${service.iconColor}`} />
                      <h2 className="text-3xl md:text-4xl font-semibold text-[#0f172a] font-['Manrope']">
                        {service.title}
                      </h2>
                    </div>
                    <p className="text-lg text-slate-600 leading-relaxed mb-8">
                      {service.description}
                    </p>
                    <Link to="/contact">
                      <Button
                        className="bg-[#243b7f] hover:bg-[#1e3a8a] text-white rounded-full px-6"
                        data-testid={`service-cta-${service.id}`}
                      >
                        Get Started
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>

                  {/* Sub-services */}
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <AnimatedContainer className="space-y-4">
                      {service.subServices.map((sub, subIndex) => (
                        <AnimatedItem key={sub.name}>
                          <Card className={`border-l-4 ${service.color.split(' ')[1]} bg-slate-50 border-y-0 border-r-0 hover:bg-white hover:shadow-lg transition-all duration-300`}>
                            <CardContent className="p-5 flex items-start gap-4">
                              <div className={`w-10 h-10 rounded-xl ${service.color.split(' ')[0]} flex items-center justify-center flex-shrink-0`}>
                                <sub.icon className={`w-5 h-5 ${service.iconColor}`} />
                              </div>
                              <div>
                                <h3 className="font-semibold text-[#0f172a] font-['Manrope'] mb-1">
                                  {sub.name}
                                </h3>
                                <p className="text-sm text-slate-600">
                                  {sub.desc}
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                        </AnimatedItem>
                      ))}
                    </AnimatedContainer>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#243b7f]" data-testid="services-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <FadeIn>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white font-['Manrope'] mb-6">
              Ready to transform your business?
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Let's discuss how our services can help you achieve your goals with cutting-edge technology solutions.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#f7c550] hover:bg-[#eab308] text-[#0f172a] rounded-full px-10 py-6 text-lg font-bold transition-all duration-300 hover:scale-105"
                data-testid="services-footer-cta"
              >
                Start a Conversation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
